<template>
    <form v-if="!requestStatus.success" @submit.prevent="onSubmit">
        <div class="row">

            <div class="col-default-12">
                <z-input v-model="formValues.name" :placeholder="formTextLocalize.infoOrder.name" name="name" ref="nameEl"
                         clearable required />
            </div>

            <div class="col-default-12">
                <z-select v-model="formValues.resident" :options="selectBoolOptions"
                          :placeholder="formTextLocalize.infoOrder.resident" ref="residentEl" required />
            </div>

            <div class="col-default-12" v-show="formValues.resident === selectBoolOptionsEnum.yes">
                <z-input v-model="formValues.inn" :placeholder="formTextLocalize.infoOrder.inn"
                         :mask="{ mask: '0000000000[00]' }" name="inn" ref="innEl" clearable required />
            </div>

            <div class="col-default-12">
                <z-textarea v-model="formValues.actionType" :placeholder="formTextLocalize.infoOrder.actionType"
                            name="actionType" ref="actionTypeEl" clearable required />
            </div>

            <div class="col-default-12">
                <z-select v-model="formValues.productsType" :options="selectProducts"
                          :placeholder="formTextLocalize.infoOrder.productsType" ref="productsTypeEl" required multiple />
            </div>

            <div class="col-default-12">
                <z-input v-model="formValues.fio" :placeholder="formTextLocalize.infoCustomer.fio" name="fio" ref="fioEl"
                         clearable required />
            </div>
        </div>

        <div class="u-mt-m">
            <h5>{{formTextLocalize.infoCustomer.labelPhone}}</h5>
        </div>
        <div class="u-mt-3xs">
            <div class="row">
                <div class="col-default-3 col-v-tablet-12">
                    <z-input v-model="formValues.code" :placeholder="formTextLocalize.infoCustomer.code" value="+7"
                             :mask="{ mask: '{+}0[00]' }" name="code" ref="codeEl" clearable required />
                </div>

                <div class="col-default-4 col-v-tablet-12">
                    <z-input v-model="formValues.phone" :placeholder="formTextLocalize.infoCustomer.phone"
                             :mask="{ mask: '(000) 000-00-00' }" name="phone" ref="phoneEl" type="tel" clearable required />
                </div>

                <div class="col-default-5 col-v-tablet-12">
                    <z-input v-model="formValues.email" :placeholder="formTextLocalize.infoCustomer.email" name="email"
                             ref="emailEl" type="email" clearable required />
                </div>
            </div>
        </div>

        <div class="u-mt-m">
            <div class="row">
                <div class="col-default-12">
                    <z-select v-model="formValues.deliveryType" :options="selectDeliveries"
                              :placeholder="formTextLocalize.infoOrder.deliveryType" ref="deliveryTypeEl" required multiple />
                </div>

                <div class="col-default-12">
                    <z-textarea v-model="formValues.comment" :placeholder="formTextLocalize.infoOrder.comment" name="comment"
                                ref="commentEl" clearable />
                </div>
            </div>
        </div>

        <div class="u-mt-m">
            <div class="row">
                <div class="col-default-12">
                    <z-checkbox :data="{
                        text: formTextLocalize.personalDataAgree,
                        id: 'personalDataAgree',
                    }" size="m" name="personalDataAgree" ref="personalDataAgreeEl" required />
                    <p class="u-mt-2xs text-size-s">{{ formTextLocalize.required }}</p>
                </div>
            </div>
        </div>

        <div class="row u-pt-2xs">
            <div class="col-default-4 col-v-tablet-12">
                <z-button :loading="requestStatus.loading" ref="submit" type="submit">{{
                    formTextLocalize.submit
                }}</z-button>

                <p v-if="!requestStatus.loading && requestStatus.error.message !== null" class="u-mt-2xs border-error-text">
                    {{ requestStatus.error.message }}
                </p>
            </div>
        </div>
    </form>

    <p v-else>{{ formTextLocalize.success }}</p>
</template>

<script setup>
import { computed, reactive, ref, onMounted } from 'vue';
import { useRecaptcha } from '@/composable/useRecaptcha';
import { formTextLocalize } from '@/views/ProductsIppPage/composable/useLocalize';

const { initGRecaptcha, getRecaptchaToken } = useRecaptcha();

const selectBoolOptionsEnum = {
    yes: formTextLocalize.boolSelectYes,
    no: formTextLocalize.boolSelectNo,
};
const selectProductsEnum = {
    lldpe: formTextLocalize.products.lldpe,
    hdpe: formTextLocalize.products.hdpe,
    tdpe: formTextLocalize.products.tdpe,
};
const selectDeliveriesEnum = {
    train: formTextLocalize.deliveries.train,
    auto: formTextLocalize.deliveries.auto,
}

const nameEl = ref(null);
const residentEl = ref(null);
const actionTypeEl = ref(null);
const productsTypeEl = ref(null);
const fioEl = ref(null);
const phoneEl = ref(null);
const emailEl = ref(null);
const deliveryTypeEl = ref(null);
const commentEl = ref(null);
const personalDataAgreeEl = ref(null);

const requestStatus = reactive({
    responsed: false,
    loading: false,
    error: {
        message: null,
    },
});

const formValues = reactive({
    name: '',
    resident: '',
    actionType: '',
    productsType: '',
    fio: '',
    phone: '',
    email: '',
    deliveryTypeEl: '',
    comment: '',
});

const requiredFields = computed(() => [
    nameEl,
    residentEl,
    actionTypeEl,
    productsTypeEl,
    fioEl,
    phoneEl,
    emailEl,
    deliveryTypeEl,
    commentEl,
    personalDataAgreeEl,
]);

const selectBoolOptions = computed(() => Object.values(selectBoolOptionsEnum).map((text, i) => ({
    'id': text,
    'selected': i === 0,
    'disabled': false,
    'text': text,
})))

const selectProducts = computed(() => Object.values(selectProductsEnum).map((text, i) => ({
    'id': text,
    'selected': i === 0,
    'disabled': false,
    'text': text,
})))

const selectDeliveries = computed(() => Object.values(selectDeliveriesEnum).map((text, i) => ({
    'id': text,
    'selected': i === 0,
    'disabled': false,
    'text': text,
})))

const createFormData = (data) => {
    return Object.entries(data)
        .reduce((formData, [key, value]) => {
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                    if (!nestedValue) return;

                    formData.append(`${key}[${nestedKey}]`, nestedValue);
                });
            } else if (Array.isArray(value)) {
                formData.append(key, value.join(', '));
            } else if (value !== '') {
                formData.append(key, value);
            }

            return formData;
        }, new FormData());
};

const onSubmit = async () => {
    let isValid = true;

    requiredFields.value.forEach((item) => {
        if (item.value) {
            item.value?.validate();
            if (item.value.error) {
                isValid = false;
            }
        }
    });

    if (!isValid) return;

    try {
        const recaptchaToken = await getRecaptchaToken();

        if (!recaptchaToken) {
            throw new Error('Failed to get reCAPTCHA token');
        }

        formValues.captcha = recaptchaToken;
        sendForm();
    } catch (error) {
        console.error(error);
    }
};

const sendForm = () => {
    requestStatus.loading = true;
    requestStatus.error.message = null;
    requestStatus.success = false;

    let xhr = new XMLHttpRequest();
    xhr.open('POST', '/local/products_ipp.php');
    xhr.send(createFormData(formValues));
    xhr.onload = function ($data) {
        console.dir($data.target.responseText);
        if (xhr.status == 200 && $data.target.responseText == 'success') {
            requestStatus.success = true;
            requestStatus.loading = false;
        } else {
            requestStatus.success = false;
            if ($data.target.responseText != '') {
                requestStatus.error.message = formTextLocalize.errorText[$data.target.responseText];
            } else requestStatus.error.message = xhr.status;
            requestStatus.loading = false;
        }
    };
};

onMounted(() => {
    initGRecaptcha();
});
</script>

<style lang="scss"></style>
